import { useEffect, useMemo, useRef, useState } from "react";
import { MenuBar } from "./components/MenuBar";
import { Sidebar } from "./components/Sidebar";
import { Toolbar } from "./components/Toolbar";
import { TrackTable } from "./components/TrackTable";
import { Inspector } from "./components/Inspector";
import { Player } from "./components/Player";
import { BatchDialog, PlaylistDialog, SettingsDialog } from "./components/LibraryDialogs";
import { InstallPrompt } from "./components/InstallPrompt";
import { AccessGate } from "./components/AccessGate";
import { MusicImportDialog } from "./components/SpotifyDialog";
import { cloudLibraryChangeKey, useLibraryStore } from "./store/libraryStore";
import { visibleTracks } from "./store/selectors";

type DialogName = "batch" | "playlist" | "settings" | "musicImport";

function exportM3u8(tracks: ReturnType<typeof visibleTracks>) {
  if (tracks.length === 0) return;
  const body = ["#EXTM3U", ...tracks.flatMap((track) => [`#EXTINF:${Math.round(track.duration)},${track.artist} - ${track.title}`, track.fileName])].join("\n");
  const url = URL.createObjectURL(new Blob([body], { type: "audio/x-mpegurl;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `needle-drop-${new Date().toISOString().slice(0, 10)}.m3u8`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function App() {
  const initialize = useLibraryStore((state) => state.initialize);
  const initialized = useLibraryStore((state) => state.initialized);
  const authenticated = useLibraryStore((state) => state.authenticated);
  const cloudSyncing = useLibraryStore((state) => state.cloudSyncing);
  const tracks = useLibraryStore((state) => state.tracks);
  const playlists = useLibraryStore((state) => state.playlists);
  const settings = useLibraryStore((state) => state.settings);
  const view = useLibraryStore((state) => state.view);
  const query = useLibraryStore((state) => state.query);
  const genre = useLibraryStore((state) => state.genreFilter);
  const label = useLibraryStore((state) => state.labelFilter);
  const sortKey = useLibraryStore((state) => state.sortKey);
  const sortDirection = useLibraryStore((state) => state.sortDirection);
  const selectedIds = useLibraryStore((state) => state.selectedIds);
  const importStatus = useLibraryStore((state) => state.importStatus);
  const notice = useLibraryStore((state) => state.notice);
  const [dialog, setDialog] = useState<DialogName>();
  const [dragging, setDragging] = useState(false);
  const [apiOnline, setApiOnline] = useState<boolean>();
  const picker = useRef<HTMLInputElement>(null);
  const visible = useMemo(() => visibleTracks(tracks, playlists, view, query, genre, label, sortKey, sortDirection), [tracks, playlists, view, query, genre, label, sortKey, sortDirection]);
  const viewTitle = view.view === "playlist"
    ? playlists.find((playlist) => playlist.id === view.value)?.name ?? "Playlist"
    : view.value ?? view.view.replace(/([A-Z])/g, " $1");

  useEffect(() => { void initialize(); }, [initialize]);
  useEffect(() => {
    if (!initialized) return;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 4500);
    void fetch(`${settings.apiBaseUrl}/api/health`, { signal: controller.signal })
      .then((response) => setApiOnline(response.ok))
      .catch(() => setApiOnline(false))
      .finally(() => window.clearTimeout(timeout));
    return () => { controller.abort(); window.clearTimeout(timeout); };
  }, [initialized, settings.apiBaseUrl]);
  useEffect(() => {
    if (!authenticated) return;
    const sync = () => { void useLibraryStore.getState().syncLibrary(true).catch(() => undefined); };
    const syncChangedLibrary = (event: StorageEvent) => {
      if (event.key === cloudLibraryChangeKey) void useLibraryStore.getState().syncLibrary(true).catch(() => undefined);
    };
    const interval = window.setInterval(sync, 30_000);
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("online", sync);
    window.addEventListener("storage", syncChangedLibrary);
    return () => { window.clearInterval(interval); document.removeEventListener("visibilitychange", sync); window.removeEventListener("online", sync); window.removeEventListener("storage", syncChangedLibrary); };
  }, [authenticated]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const editing = target.matches("input, textarea, select, [contenteditable=true]");
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "f") { event.preventDefault(); document.querySelector<HTMLInputElement>("#library-search")?.focus(); }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "a" && !editing) { event.preventDefault(); useLibraryStore.getState().setSelection(visible.map((track) => track.id)); }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "o") { event.preventDefault(); picker.current?.click(); }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "e") { event.preventDefault(); exportM3u8(selectedIds.length ? tracks.filter((track) => selectedIds.includes(track.id)) : visible); }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b" && selectedIds.length) { event.preventDefault(); setDialog("batch"); }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "t" && selectedIds.length) { event.preventDefault(); void useLibraryStore.getState().enrichTracks(selectedIds); }
      if (event.key === " " && !editing) { event.preventDefault(); window.dispatchEvent(new Event("needle-toggle-play")); }
      if (event.key === "Enter" && !editing && selectedIds[0]) useLibraryStore.getState().setCurrentTrack(selectedIds[0]);
      if ((event.key === "Delete" || event.key === "Backspace") && !editing && selectedIds.length) confirmDelete(selectedIds);
      if (event.key === "Escape") useLibraryStore.getState().setSelection([]);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedIds, tracks, visible]);

  useEffect(() => {
    if (!notice) return;
    const timeout = window.setTimeout(() => useLibraryStore.getState().clearNotice(), 5000);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  function receiveFiles(fileList: FileList | File[]) { void useLibraryStore.getState().importFiles(Array.from(fileList)); }
  function confirmDelete(ids: string[]) {
    const uniqueIds = [...new Set(ids)].filter((id) => tracks.some((track) => track.id === id));
    if (!uniqueIds.length) return;
    const description = uniqueIds.length === 1
      ? `“${tracks.find((track) => track.id === uniqueIds[0])?.title || "Untitled"}”`
      : `${uniqueIds.length} selected tracks`;
    if (window.confirm(`Permanently delete ${description} from the cloud library?\n\nThe MP3 files will be removed from private cloud storage on every device. This cannot be undone.`)) {
      void useLibraryStore.getState().removeTracks(uniqueIds);
    }
  }
  function confirmDeletePlaylist(id: string) {
    const playlist = playlists.find((item) => item.id === id);
    if (!playlist) return;
    if (window.confirm(`Delete the playlist “${playlist.name}”?\n\nIts tracks remain in the private cloud library and in every other playlist.`)) {
      void useLibraryStore.getState().deletePlaylist(id);
    }
  }
  if (!initialized) return <div className="boot-screen"><div className="boot-logo">ND</div><p>OPENING LIBRARY DATABASE…</p><span>PLEASE WAIT</span></div>;
  if (!authenticated) return <AccessGate />;

  return <div className={`app-shell${settings.subtleCrt ? " crt" : ""}`} onDragEnter={(event) => { event.preventDefault(); setDragging(true); }} onDragOver={(event) => event.preventDefault()} onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false); }} onDrop={(event) => { event.preventDefault(); setDragging(false); receiveFiles(event.dataTransfer.files); }}>
    <MenuBar onImport={receiveFiles} onOpenMusicImport={() => setDialog("musicImport")} onOpenSettings={() => setDialog("settings")} onNewPlaylist={() => setDialog("playlist")} onBatchEdit={() => setDialog("batch")} onExport={() => exportM3u8(selectedIds.length ? tracks.filter((track) => selectedIds.includes(track.id)) : visible)} onDelete={confirmDelete} />
    <div className="desktop-title"><span>NEEDLE DROP MUSIC LIBRARY</span><div className="desktop-status"><i className={apiOnline ? "online" : ""} /> PRIVATE CLOUD · {cloudSyncing ? "SYNCING" : apiOnline === undefined ? "CHECKING" : apiOnline ? "ONLINE" : "OFFLINE"}</div></div>
    <Toolbar onImport={() => picker.current?.click()} onBatchEdit={() => setDialog("batch")} onDelete={confirmDelete} onOpenMusicImport={() => setDialog("musicImport")} onNewPlaylist={() => setDialog("playlist")} />
    <div className="workspace">
      <Sidebar onNewPlaylist={() => setDialog("playlist")} onDeletePlaylist={confirmDeletePlaylist} />
      <main className="library-pane"><div className="pane-title library-title"><span>{viewTitle.toUpperCase()}</span><span>{visible.length} RECORDS / SORT: {sortKey.toUpperCase()} {sortDirection === "asc" ? "▲" : "▼"}</span></div><TrackTable tracks={visible} onBatchEdit={() => setDialog("batch")} onDelete={confirmDelete} /></main>
      <Inspector />
    </div>
    <Player queue={visible} />
    <input ref={picker} className="visually-hidden" type="file" accept="audio/mpeg,.mp3,application/zip,.zip" multiple onChange={(event) => receiveFiles(event.target.files ?? [])} />
    {dragging && <div className="drop-overlay"><div><strong>DROP MP3 OR ZIP</strong><span>UPLOAD TO PRIVATE CLOUD LIBRARY</span></div></div>}
    {importStatus && <div className="import-progress"><strong>{importStatus.action} {importStatus.current}/{importStatus.total}</strong><span title={importStatus.fileName}>{importStatus.fileName}</span><div><i style={{ width: `${(importStatus.current / importStatus.total) * 100}%` }} /></div></div>}
    {notice && <button className="notice" onClick={() => useLibraryStore.getState().clearNotice()}>{notice}</button>}
    <InstallPrompt />
    {dialog === "batch" && <BatchDialog ids={selectedIds} onClose={() => setDialog(undefined)} />}
    {dialog === "playlist" && <PlaylistDialog onClose={() => setDialog(undefined)} />}
    {dialog === "settings" && <SettingsDialog onClose={() => setDialog(undefined)} />}
    {dialog === "musicImport" && <MusicImportDialog onClose={() => setDialog(undefined)} onImport={receiveFiles} />}
  </div>;
}
